<template>
  <div>
    <base-header class="pb-6">
      <b-row align-v="center" class="py-4 justify-content-center">
        <div>
          <h1 class="text-white d-inline-block mb-0">Let's get started.</h1>
        </div>
      </b-row>
    </base-header>

    <b-container fluid class="mt--6 setup-container">
      <b-row class="justify-content-center">
        <b-col lg="8" class="card-wrapper">
          <!-- Grid system -->
          <card class="card">
            <template slot="header">
              <b-badge variant="info">1 / 4</b-badge>
              <h3 class="mb-0">Claim a URL for your status page.</h3>
              <p class="text-sm mb-0">
                This is commonly your website or app name. This will be the URL
                you can provide your users to see your status page.
              </p>
            </template>
            <b-row>
              <b-col sm>
                <validation-observer
                  v-slot="{ handleSubmit }"
                  ref="formValidator"
                >
                  <b-form
                    role="form"
                    @submit.prevent="handleSubmit(websiteUrlChosen)"
                  >
                    <base-input
                      input-classes="form-control-lg"
                      v-model="websiteUrl"
                      rules="alpha_dash"
                      v-on:submit-clicked="handleSubmit(websiteUrlChosen)"
                      append-button="Next step"
                      placeholder="AmazingStartupIncorporated"
                      name="URL"
                      @keyup.enter.native="handleSubmit(websiteUrlChosen)"
                    >
                      <small slot="prepend">
                        https://boltscale.io/status/
                      </small>
                    </base-input>
                  </b-form>
                </validation-observer>
              </b-col>
            </b-row>

            <fade-transition :duration="200">
              <b-row v-if="this.websiteUrlSubmitted && websiteUrl.length > 0">
                <b-col sm>
                  <i style="color: #2dce89;" class="fas fa-check-circle"></i>
                  {{ websiteUrl }} chosen!
                </b-col>
              </b-row>
            </fade-transition>
          </card>
        </b-col>
      </b-row>

      <fade-transition :duration="200">
        <b-row
          class="justify-content-center"
          v-if="websiteUrl && websiteUrlSubmitted"
        >
          <b-col lg="8" class="card-wrapper">
            <!-- Grid system -->
            <card class="card">
              <template slot="header">
                <b-badge variant="info">2 / 4</b-badge>
                <span>
                  <h3 class="mb-0">Now, let's add a component.</h3>
                </span>
                <p class="text-sm mb-0">
                  A
                  <mark>
                    <strong style="font-weight: 600;">component</strong>
                  </mark>
                  is an endpoint you want us to monitor (such as an API or a
                  website). Components are pinged up to every minute, with the
                  performance (and any detected problems) displayed on your
                  status page or alerted to you.
                </p>
              </template>
              <b-row class="form-group justify-content-center">
                <b-col cols="12" md="6">
                  <base-input
                    :disabled="customEndpoint.submitted"
                    label="Name"
                    prepend-icon="fas fa-rocket"
                    v-on:submit-clicked="submitCustomEndpoint"
                    input-classes="form-control-lg"
                    placeholder="Your website"
                    type="url"
                    v-model="customEndpoint.name"
                    @keyup.enter.native="submitCustomEndpoint"
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <base-input
                    :disabled="customEndpoint.submitted"
                    label="Endpoint"
                    prepend-icon="fas fa-rocket"
                    v-on:submit-clicked="submitCustomEndpoint"
                    input-classes="form-control-lg"
                    placeholder="https://yourwebsite.com"
                    type="url"
                    v-model="customEndpoint.url"
                    @keyup.enter.native="submitCustomEndpoint"
                  />
                </b-col>
                <base-button
                  v-if="!customEndpoint.submitted"
                  @click="submitCustomEndpoint"
                  icon
                  type="primary"
                  size="md"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-database"></i>
                  </span>
                  <span class="btn-inner--text">Add Component</span>
                </base-button>

                <base-button
                  v-else
                  @click="removeCustomEndpoint"
                  icon
                  type="primary"
                  size="md"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-database"></i>
                  </span>
                  <span class="btn-inner--text">Remove Component</span>
                </base-button>
              </b-row>
              <b-row v-if="this.customEndpoint.submitted">
                <b-col sm>
                  <b-card-text class="mb-4">
                    <i style="color: #2dce89;" class="fas fa-check-circle"></i>
                    Response time of
                    <span style="font-weight: 600;">
                      {{ customEndpoint.duration }} ms
                    </span>
                    measured to {{ customEndpoint.url }}.
                  </b-card-text>
                </b-col>
              </b-row>
            </card>

            <paginated-tables
              v-if="
                this.customEndpoint.submitted || this.selectedRows.length > 0
              "
              v-on:apiSelected="apiSelected"
              :disabled="componentLimitReached"
            ></paginated-tables>
          </b-col>
        </b-row>
      </fade-transition>

      <b-row
        class="justify-content-center"
        v-if="
          websiteUrl &&
          websiteUrlSubmitted &&
          (this.customEndpoint.submitted || this.selectedRows.length > 0)
        "
      >
        <b-col lg="8" class="card-wrapper">
          <!-- Grid system -->
          <card class="card">
            <template slot="header">
              <b-badge variant="info">4 / 4</b-badge>
              <h3 class="mb-0">Finish!</h3>

              <p
                v-if="
                  !this.customEndpoint.submitted &&
                  this.selectedRows.length == 0
                "
                class="text-sm mb-0"
              >
                Select some components to monitor above before you can move
                forward.
              </p>

              <p v-else class="text-sm mb-0">
                You've chosen to monitor
                {{ this.customEndpoint.submitted ? "1" : "0" }} custom component
                and {{ this.selectedRows.length }} third-party components. Don't
                worry, you can always change these later.
              </p>
            </template>
            <b-row
              v-if="
                this.customEndpoint.submitted || this.selectedRows.length != 0
              "
            >
              <b-col sm>
                <base-button
                  @click="nextStep"
                  icon
                  type="primary"
                  size="lg"
                  :disabled="submittingInProgress"
                  :loading="submittingInProgress"
                >
                  <span class="btn-inner--icon">
                    <i v-if="!submittingInProgress" class="ni ni-bag-17"></i>
                  </span>
                  <span class="btn-inner--text">
                    Show me my dashboard!
                  </span>
                </base-button>
              </b-col>
            </b-row>
          </card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import * as chartConfigs from "@/components/Charts/config";
import PaginatedTables from "@/views/Tables/PaginatedTables";
import { FadeTransition } from "vue2-transitions";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  components: {
    PaginatedTables,
    FadeTransition
  },
  data() {
    return {
      name: "",
      submittingInProgress: false,
      components: [],
      componentLimitReached: false,
      website: {},
      websiteUrl: "",
      websiteUrlSubmitted: false,
      addedCustomEndpoints: [],
      totalComponents: 0,
      customEndpoint: {
        submitted: false,
        url: "",
        name: "",
        duration: null
      },
      dependencies: [],
      selectedRows: [],
      okay: [],
      pings: [
        {
          createdAt: ""
        }
      ],
      url: ""
    };
  },
  methods: {
    removeEndpoint(index) {
      console.log(name);
      console.log(this.addedCustomEndpoints);
      this.addedCustomEndpoints.splice(index, 1);
    },
    // TODO: If the validation passes, this method is called. However, if the user
    // then changes the website again, the v-model changes the text that will
    // get submitted!
    async websiteUrlChosen() {
      console.log("Great, project name is chosen!");
      const res = await this.$http.get(
        `/website/url/available/${this.websiteUrl}`
      );
      console.log(res);
      if (!res.data || !res.data.success) return;
      console.log("Awesome!");
      this.websiteUrlSubmitted = true;
      this.$gtag.event("submit_website_url", {
        event_category: "setup",
        event_label: this.websiteUrl
      });
    },

    async nextStep() {
      this.submittingInProgress = true;
      this.$gtag.event("submit_setup", {
        event_category: "setup"
      });

      console.log("Submitting...");
      const thirdPartyApis = this.selectedRows;

      console.log(thirdPartyApis);
      console.log(this.customEndpoint);

      const resp = await this.$http.post("/website/new", {
        thirdPartyApis: thirdPartyApis,
        customEndpoint: this.customEndpoint,
        websiteUrl: this.websiteUrl
      });
      this.submittingInProgress = false;
      console.log("Response received!");
      console.log(resp);
      if (resp.data.success) {
        await this.auth();
        this.$router.push("/app/dashboard");
      }
    },
    apiSelected(allSelectedRows) {
      this.totalComponents = allSelectedRows.length;
      if (this.customEndpoint.submitted) {
        this.totalComponents += 1;
      }

      console.log("An api was seleted!");
      this.selectedRows = allSelectedRows;
      this.$gtag.event("select_api", {
        event_category: "setup",
        event_label: allSelectedRows.length
      });
    },
    removeCustomEndpoint(ev) {
      this.$gtag.event("remove_custom_endpoint", {
        event_category: "setup",
        event_label: this.customEndpoint.url,
        name: this.customEndpoint.name
      });

      this.customEndpoint.submitted = false;
      this.customEndpoint.duration = null;
      this.totalComponents--;
    },
    submitCustomEndpoint(ev) {
      this.$gtag.event("submit_custom_endpoint", {
        event_category: "setup",
        event_label:
          "name_" +
          this.customEndpoint.name +
          "_url_" +
          this.customEndpoint.url,
        name: this.customEndpoint.name,
        url: this.customEndpoint.url
      });

      console.log("Submitted!");
      console.log(ev);
      console.log(this.customEndpoint.name);
      console.log("Submitting custom endpint....");
      this.$http
        .post("/latency", { url: this.customEndpoint.url })
        .then((resp) => {
          if (!resp.data || !resp.data.success) return;
          console.log("Response received");
          console.log(resp);
          this.customEndpoint.submitted = true;
          this.customEndpoint.duration = resp.data.data.duration;
          console.log(this.totalComponents);
          this.totalComponents++;
          console.log(this.totalComponents);
        });
    },
    ...mapActions("user", ["auth"])
  },
  watch: {
    totalComponents: function (newVal, oldVal) {
      console.log("Here is fucking new val for htis");
      console.log(newVal);
      if (newVal >= 3) {
        this.componentLimitReached = true;
        this.$toasted.show(
          "You can only monitor 3 components on the free plan.  <br/> <a href='/pricing'>Upgrade now</a> to monitor more.",
          {
            type: "warn",
            duration: 5000
          }
        );
      } else {
        this.componentLimitReached = false;
      }
    },
    "customEndpoint.name": function (val) {
      this.$gtag.event("type_custom_endpoint_name", {
        event_category: "setup",
        event_label: val
      });
    },
    "customEndpoint.url": function (val) {
      this.$gtag.event("type_custom_endpoint_url", {
        event_category: "setup",
        event_label: val
      });
    }
  },
  mounted() {}
};
</script>
<style>
.navbar-collapse {
  background-color: transparent;
}

.setup-container {
  background-color: #f6f9fc;
}
.profile-header {
  /*background-image: url(/img/theme/profile-cover.jpg);*/
  background-size: cover;
  background-position: center top;
  min-height: 350px;
}
.monitor-card {
  background: #5e72e4 !important;
}

.monitor-card.card-success {
  background: #2bce89 !important;
}

.endpoint-success {
  color: #2bce89;
  background-color: #f8f9fe;
  border-radius: 100px;
  padding: 5px;
}

.setup-container .badge {
  position: absolute;
  left: -18px;
  top: -9px;
}
</style>
